import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@shared';
import { Button, type ButtonProps } from '@/components/ui/Button';

export type DaysheetlyButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'destructive'
  | 'ghost'
  | 'link';

export type DaysheetlyButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface DaysheetlyButtonProps
  extends Omit<ButtonProps, 'variant' | 'size' | 'children'> {
  variant?: DaysheetlyButtonVariant;
  size?: DaysheetlyButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children?: React.ReactNode;
}

const primitiveVariants: Record<DaysheetlyButtonVariant, ButtonProps['variant']> = {
  primary: 'default',
  secondary: 'secondary',
  outline: 'outline',
  destructive: 'destructive',
  ghost: 'ghost',
  link: 'link',
};

const primitiveSizes: Record<DaysheetlyButtonSize, ButtonProps['size']> = {
  sm: 'sm',
  md: 'default',
  lg: 'lg',
  icon: 'icon',
};

const variantClasses: Record<DaysheetlyButtonVariant, string> = {
  primary: 'rounded-xl bg-primary text-primary-foreground hover:bg-primary-hover',
  secondary: 'rounded-xl bg-secondary text-secondary-foreground hover:bg-secondary/80',
  outline:
    'rounded-xl border-primary bg-background text-primary hover:bg-primary/5 hover:text-primary',
  destructive: 'rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90',
  ghost: 'rounded-xl hover:bg-accent hover:text-accent-foreground',
  link: 'rounded-none text-primary underline-offset-4 hover:underline',
};

const sizeClasses: Record<DaysheetlyButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
  icon: 'h-10 w-10 p-0',
};

const DaysheetlyButton = React.forwardRef<HTMLButtonElement, DaysheetlyButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      startIcon,
      endIcon,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => (
    <Button
      ref={ref}
      variant={primitiveVariants[variant]}
      size={primitiveSizes[size]}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'gap-2 font-medium transition-all',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : startIcon}
      {children}
      {!loading ? endIcon : null}
    </Button>
  ),
);

DaysheetlyButton.displayName = 'DaysheetlyButton';

export { DaysheetlyButton };
